import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Plus, PencilSimple, Trash, X, Check, FloppyDisk, Eye, FilmStrip } from "@phosphor-icons/react";

const fieldClass = "w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-body bg-white text-slate-800 focus:outline-none focus:border-[#C4A265]";
const labelClass = "block text-[11px] uppercase tracking-wider font-body text-slate-500 mb-1";

function FormField({ label, children }) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      {children}
    </div>
  );
}

function ActionRow({ onEdit, onDelete, extra }) {
  return (
    <div className="flex items-center gap-1">
      {extra}
      <button onClick={onEdit} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Edit" data-testid="row-edit">
        <PencilSimple size={14} />
      </button>
      <button onClick={onDelete} className="p-1.5 rounded hover:bg-red-50 text-red-500" title="Delete" data-testid="row-delete">
        <Trash size={14} />
      </button>
    </div>
  );
}

function Modal({ open, onClose, title, children, onSave, saving }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-edit-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-heading text-lg text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" aria-label="Close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">{children}</div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={onSave} disabled={saving} className="rounded-full bg-[#C4A265] hover:bg-[#B8965C] text-white" data-testid="admin-save-btn">
            <FloppyDisk size={14} className="mr-1" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------- LEARN ----------

function LearnTab() {
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {mode: "create"|"edit", path}
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [openLessonsFor, setOpenLessonsFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/learn/paths"); setPaths(r.data.items); }
    catch { toast.error("Couldn't load paths"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setForm({ slug: "", name: "", description: "", icon: "BookOpen", color: "path-warm", is_foundation: false, is_locked: false, order: paths.length + 1 });
    setEditing({ mode: "create" });
  };
  const startEdit = (p) => {
    setForm({ ...p });
    setEditing({ mode: "edit", path: p });
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing.mode === "create") {
        if (!form.slug || !form.name) { toast.error("Slug + name required"); setSaving(false); return; }
        await api.post("/admin/learn/paths", form);
        toast.success("Path created");
      } else {
        const { id, slug, lesson_count, total_lessons, completed_lessons, ...patch } = form;
        await api.put(`/admin/learn/paths/${editing.path.id}`, patch);
        toast.success("Path updated");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}" and all its lessons?`)) return;
    try { await api.delete(`/admin/learn/paths/${p.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-learn-tab">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-heading text-lg text-slate-900">Learning Paths ({paths.length})</h3>
        <Button onClick={startCreate} className="rounded-full bg-[#C4A265] hover:bg-[#B8965C] text-white" data-testid="admin-new-path-btn">
          <Plus size={14} className="mr-1" /> New Path
        </Button>
      </div>
      {loading ? <p className="text-sm text-slate-500">Loading…</p> : paths.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-12">No paths yet. Create the first one.</p>
      ) : (
        <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Slug</th><th className="text-center px-3 py-2">Lessons</th><th className="text-center px-3 py-2">Foundation</th><th className="px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {paths.map((p) => (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`path-row-${p.slug}`}>
                  <td className="px-3 py-2 text-slate-900 font-medium">{p.name}</td>
                  <td className="px-3 py-2 text-slate-500"><code className="text-xs">{p.slug}</code></td>
                  <td className="px-3 py-2 text-center text-slate-700">{p.lesson_count}</td>
                  <td className="px-3 py-2 text-center">{p.is_foundation ? <Check size={14} className="inline text-emerald-500" /> : ""}</td>
                  <td className="px-3 py-2">
                    <ActionRow
                      onEdit={() => startEdit(p)}
                      onDelete={() => remove(p)}
                      extra={
                        <button onClick={() => setOpenLessonsFor(p)} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Lessons" data-testid={`path-lessons-${p.slug}`}>
                          <Eye size={14} />
                        </button>
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        onSave={save}
        saving={saving}
        title={editing?.mode === "create" ? "New Learning Path" : `Edit "${editing?.path?.name}"`}
      >
        {editing?.mode === "create" && (
          <FormField label="Slug (url-friendly id)">
            <input className={fieldClass} value={form.slug || ""} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="form-slug" />
          </FormField>
        )}
        <FormField label="Name"><input className={fieldClass} value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" /></FormField>
        <FormField label="Description"><textarea className={fieldClass} rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="form-desc" /></FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Icon (BookOpen, HandsPraying…)"><input className={fieldClass} value={form.icon || ""} onChange={(e) => setForm({ ...form, icon: e.target.value })} /></FormField>
          <FormField label="Color (path-warm, path-rose…)"><input className={fieldClass} value={form.color || ""} onChange={(e) => setForm({ ...form, color: e.target.value })} /></FormField>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <FormField label="Order"><input type="number" className={fieldClass} value={form.order ?? 0} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} /></FormField>
          <label className="flex items-center gap-2 text-sm text-slate-700 mt-6"><input type="checkbox" checked={!!form.is_foundation} onChange={(e) => setForm({ ...form, is_foundation: e.target.checked })} /> Foundation</label>
          <label className="flex items-center gap-2 text-sm text-slate-700 mt-6"><input type="checkbox" checked={!!form.is_locked} onChange={(e) => setForm({ ...form, is_locked: e.target.checked })} /> Locked</label>
        </div>
      </Modal>

      <LessonsModal path={openLessonsFor} onClose={() => { setOpenLessonsFor(null); load(); }} />
    </div>
  );
}

function LessonsModal({ path, onClose }) {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!path) return;
    setLoading(true);
    try { const r = await api.get(`/admin/learn/paths/${path.id}/lessons`); setLessons(r.data.items); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [path?.id]);

  const startCreate = () => {
    setForm({ slug: "", title: "", scripture: "", content: "", duration_min: 10, order: lessons.length + 1 });
    setEditing({ mode: "create" });
  };
  const startEdit = (l) => { setForm({ ...l }); setEditing({ mode: "edit", lesson: l }); };
  const save = async () => {
    setSaving(true);
    try {
      if (editing.mode === "create") {
        if (!form.slug || !form.title) { toast.error("Slug + title required"); setSaving(false); return; }
        await api.post(`/admin/learn/paths/${path.id}/lessons`, form);
        toast.success("Lesson created");
      } else {
        const { id, slug, path_id, ...patch } = form;
        await api.put(`/admin/learn/lessons/${editing.lesson.id}`, patch);
        toast.success("Lesson updated");
      }
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const remove = async (l) => {
    if (!window.confirm(`Delete lesson "${l.title}"?`)) return;
    await api.delete(`/admin/learn/lessons/${l.id}`); toast.success("Deleted"); load();
  };

  if (!path) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="lessons-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-heading text-lg text-slate-900">Lessons in “{path.name}”</h3>
            <p className="text-xs text-slate-500">{lessons.length} lesson{lessons.length === 1 ? "" : "s"}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={startCreate} className="rounded-full bg-[#C4A265] hover:bg-[#B8965C] text-white" data-testid="new-lesson-btn">
              <Plus size={14} className="mr-1" /> New
            </Button>
            <button onClick={onClose} className="text-slate-500 hover:text-slate-900" aria-label="Close"><X size={18} /></button>
          </div>
        </div>
        <div className="p-5">
          {loading ? <p className="text-sm text-slate-500">Loading…</p> : lessons.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-10">No lessons yet.</p>
          ) : (
            <div className="space-y-2">
              {lessons.map((l) => (
                <div key={l.id} className="border border-slate-200 rounded-lg p-3 flex items-center gap-3" data-testid={`lesson-row-${l.slug}`}>
                  <span className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-600">{l.order}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{l.title}</p>
                    <p className="text-xs text-slate-500">{l.scripture || "—"} · {l.duration_min} min</p>
                  </div>
                  <ActionRow onEdit={() => startEdit(l)} onDelete={() => remove(l)} />
                </div>
              ))}
            </div>
          )}
        </div>

        <Modal open={!!editing} onClose={() => setEditing(null)} onSave={save} saving={saving}
          title={editing?.mode === "create" ? "New Lesson" : `Edit lesson`}>
          {editing?.mode === "create" && (
            <FormField label="Slug"><input className={fieldClass} value={form.slug || ""} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></FormField>
          )}
          <FormField label="Title"><input className={fieldClass} value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} /></FormField>
          <FormField label="Scripture reference (e.g. John 3:16)"><input className={fieldClass} value={form.scripture || ""} onChange={(e) => setForm({ ...form, scripture: e.target.value })} /></FormField>
          <FormField label="Content"><textarea className={fieldClass} rows={6} value={form.content || ""} onChange={(e) => setForm({ ...form, content: e.target.value })} /></FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Duration (min)"><input type="number" className={fieldClass} value={form.duration_min || 0} onChange={(e) => setForm({ ...form, duration_min: Number(e.target.value) })} /></FormField>
            <FormField label="Order"><input type="number" className={fieldClass} value={form.order || 0} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} /></FormField>
          </div>
        </Modal>
      </div>
    </div>
  );
}

// ---------- COMMUNITY ----------

function CommunityTab() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/community/groups"); setGroups(r.data.items); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setForm({ slug: "", name: "", description: "", category: "General", order: groups.length + 1 }); setEditing({ mode: "create" }); };
  const startEdit = (g) => { setForm({ ...g }); setEditing({ mode: "edit", group: g }); };
  const save = async () => {
    setSaving(true);
    try {
      if (editing.mode === "create") {
        if (!form.slug || !form.name) { toast.error("Slug + name required"); setSaving(false); return; }
        await api.post("/admin/community/groups", form);
      } else {
        const { id, slug, member_count, ...patch } = form;
        await api.put(`/admin/community/groups/${editing.group.id}`, patch);
      }
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const remove = async (g) => {
    if (!window.confirm(`Delete "${g.name}"?`)) return;
    await api.delete(`/admin/community/groups/${g.id}`); toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-community-tab">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-heading text-lg text-slate-900">Community Groups ({groups.length})</h3>
        <Button onClick={startCreate} className="rounded-full bg-[#C4A265] hover:bg-[#B8965C] text-white"><Plus size={14} className="mr-1" /> New Group</Button>
      </div>
      {loading ? <p className="text-sm text-slate-500">Loading…</p> : groups.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-12">No groups yet.</p>
      ) : (
        <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Category</th><th className="text-center px-3 py-2">Members</th><th className="px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <tr key={g.id} className="border-t border-slate-100" data-testid={`group-row-${g.slug}`}>
                  <td className="px-3 py-2 font-medium text-slate-900">{g.name}</td>
                  <td className="px-3 py-2 text-slate-500">{g.category}</td>
                  <td className="px-3 py-2 text-center text-slate-700">{g.member_count}</td>
                  <td className="px-3 py-2"><ActionRow onEdit={() => startEdit(g)} onDelete={() => remove(g)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal open={!!editing} onClose={() => setEditing(null)} onSave={save} saving={saving}
        title={editing?.mode === "create" ? "New Group" : `Edit "${editing?.group?.name}"`}>
        {editing?.mode === "create" && <FormField label="Slug"><input className={fieldClass} value={form.slug || ""} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></FormField>}
        <FormField label="Name"><input className={fieldClass} value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormField>
        <FormField label="Description"><textarea className={fieldClass} rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Category"><input className={fieldClass} value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} /></FormField>
          <FormField label="Order"><input type="number" className={fieldClass} value={form.order || 0} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} /></FormField>
        </div>
      </Modal>
    </div>
  );
}

// ---------- WATCH ----------

function WatchTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => { setLoading(true); try { const r = await api.get("/admin/watch/content"); setItems(r.data.items); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setForm({ slug: "", title: "", description: "", speaker: "", duration: "30 min", category: "Teaching", color: "path-warm", is_live: false, is_featured: false, video_url: "", order: items.length + 1 }); setEditing({ mode: "create" }); };
  const startEdit = (it) => { setForm({ ...it }); setEditing({ mode: "edit", item: it }); };
  const save = async () => {
    setSaving(true);
    try {
      if (editing.mode === "create") {
        if (!form.slug || !form.title) { toast.error("Slug + title required"); setSaving(false); return; }
        await api.post("/admin/watch/content", form);
      } else {
        const { id, slug, ...patch } = form;
        await api.put(`/admin/watch/content/${editing.item.id}`, patch);
      }
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.title}"?`)) return;
    await api.delete(`/admin/watch/content/${it.id}`); toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-watch-tab">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-heading text-lg text-slate-900">Watch Library ({items.length})</h3>
        <Button onClick={startCreate} className="rounded-full bg-[#C4A265] hover:bg-[#B8965C] text-white"><Plus size={14} className="mr-1" /> New Item</Button>
      </div>
      {loading ? <p className="text-sm text-slate-500">Loading…</p> : items.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-12">No content yet.</p>
      ) : (
        <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr><th className="text-left px-3 py-2">Title</th><th className="text-left px-3 py-2">Speaker</th><th className="text-left px-3 py-2">Category</th><th className="text-center px-3 py-2">Live</th><th className="text-center px-3 py-2">Featured</th><th className="text-center px-3 py-2">Video</th><th className="px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-slate-100" data-testid={`watch-row-${it.slug}`}>
                  <td className="px-3 py-2 font-medium text-slate-900">{it.title}</td>
                  <td className="px-3 py-2 text-slate-500">{it.speaker}</td>
                  <td className="px-3 py-2 text-slate-500">{it.category}</td>
                  <td className="px-3 py-2 text-center">{it.is_live ? <Check size={14} className="inline text-red-500" /> : ""}</td>
                  <td className="px-3 py-2 text-center">{it.is_featured ? <Check size={14} className="inline text-emerald-500" /> : ""}</td>
                  <td className="px-3 py-2 text-center">{it.video_url ? <FilmStrip size={14} className="inline text-[#C4A265]" /> : <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-2"><ActionRow onEdit={() => startEdit(it)} onDelete={() => remove(it)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal open={!!editing} onClose={() => setEditing(null)} onSave={save} saving={saving}
        title={editing?.mode === "create" ? "New Watch Item" : `Edit "${editing?.item?.title}"`}>
        {editing?.mode === "create" && <FormField label="Slug"><input className={fieldClass} value={form.slug || ""} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></FormField>}
        <FormField label="Title"><input className={fieldClass} value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} /></FormField>
        <FormField label="Description"><textarea className={fieldClass} rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Speaker"><input className={fieldClass} value={form.speaker || ""} onChange={(e) => setForm({ ...form, speaker: e.target.value })} /></FormField>
          <FormField label="Duration"><input className={fieldClass} value={form.duration || ""} onChange={(e) => setForm({ ...form, duration: e.target.value })} /></FormField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Category"><input className={fieldClass} value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} /></FormField>
          <FormField label="Order"><input type="number" className={fieldClass} value={form.order || 0} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} /></FormField>
        </div>
        <FormField label="Video URL (YouTube, Vimeo, or .mp4)"><input className={fieldClass} placeholder="https://www.youtube.com/watch?v=…" value={form.video_url || ""} onChange={(e) => setForm({ ...form, video_url: e.target.value })} /></FormField>
        <div className="flex items-center gap-5">
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={!!form.is_live} onChange={(e) => setForm({ ...form, is_live: e.target.checked })} /> Live now</label>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={!!form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} /> Featured</label>
        </div>
      </Modal>
    </div>
  );
}

// ---------- PRAYER JOURNEYS ----------

function JourneysTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => { setLoading(true); try { const r = await api.get("/admin/prayer-journeys"); setItems(r.data.items); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setForm({ slug: "", name: "", description: "", icon: "HandsPraying", color: "path-warm", steps: [{ title: "Prepare", text: "" }], order: items.length + 1 }); setEditing({ mode: "create" }); };
  const startEdit = (it) => { setForm({ ...it, steps: it.steps?.length ? it.steps : [{ title: "Prepare", text: "" }] }); setEditing({ mode: "edit", item: it }); };
  const save = async () => {
    setSaving(true);
    try {
      if (editing.mode === "create") {
        if (!form.slug || !form.name) { toast.error("Slug + name required"); setSaving(false); return; }
        await api.post("/admin/prayer-journeys", form);
      } else {
        const { id, slug, ...patch } = form;
        await api.put(`/admin/prayer-journeys/${editing.item.id}`, patch);
      }
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name}"?`)) return;
    await api.delete(`/admin/prayer-journeys/${it.id}`); toast.success("Deleted"); load();
  };

  const setStep = (i, patch) => setForm((f) => ({ ...f, steps: f.steps.map((s, idx) => idx === i ? { ...s, ...patch } : s) }));
  const addStep = () => setForm((f) => ({ ...f, steps: [...(f.steps || []), { title: "", text: "" }] }));
  const removeStep = (i) => setForm((f) => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) }));
  const moveStep = (i, dir) => setForm((f) => {
    const j = i + dir;
    if (j < 0 || j >= f.steps.length) return f;
    const next = [...f.steps];
    [next[i], next[j]] = [next[j], next[i]];
    return { ...f, steps: next };
  });

  return (
    <div data-testid="admin-journeys-tab">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-heading text-lg text-slate-900">Prayer Journeys ({items.length})</h3>
        <Button onClick={startCreate} className="rounded-full bg-[#C4A265] hover:bg-[#B8965C] text-white"><Plus size={14} className="mr-1" /> New Journey</Button>
      </div>
      {loading ? <p className="text-sm text-slate-500">Loading…</p> : items.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-12">No journeys yet.</p>
      ) : (
        <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Slug</th><th className="text-center px-3 py-2">Steps</th><th className="px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-slate-100" data-testid={`journey-row-${it.slug}`}>
                  <td className="px-3 py-2 font-medium text-slate-900">{it.name}</td>
                  <td className="px-3 py-2 text-slate-500"><code className="text-xs">{it.slug}</code></td>
                  <td className="px-3 py-2 text-center text-slate-700">{(it.steps || []).length}</td>
                  <td className="px-3 py-2"><ActionRow onEdit={() => startEdit(it)} onDelete={() => remove(it)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal open={!!editing} onClose={() => setEditing(null)} onSave={save} saving={saving}
        title={editing?.mode === "create" ? "New Journey" : `Edit "${editing?.item?.name}"`}>
        {editing?.mode === "create" && <FormField label="Slug"><input className={fieldClass} value={form.slug || ""} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></FormField>}
        <FormField label="Name"><input className={fieldClass} value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormField>
        <FormField label="Description"><textarea className={fieldClass} rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormField>
        <div className="grid grid-cols-3 gap-3">
          <FormField label="Icon"><input className={fieldClass} value={form.icon || ""} onChange={(e) => setForm({ ...form, icon: e.target.value })} /></FormField>
          <FormField label="Color"><input className={fieldClass} value={form.color || ""} onChange={(e) => setForm({ ...form, color: e.target.value })} /></FormField>
          <FormField label="Order"><input type="number" className={fieldClass} value={form.order || 0} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} /></FormField>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={labelClass}>Steps ({(form.steps || []).length})</label>
            <button type="button" onClick={addStep} className="text-xs text-[#C4A265] font-medium hover:text-[#5C4033]">+ Add step</button>
          </div>
          <div className="space-y-2">
            {(form.steps || []).map((s, i) => (
              <div key={i} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-slate-500 w-8">#{i + 1}</span>
                  <input className={`${fieldClass} flex-1`} placeholder="Step title (e.g. Prepare)" value={s.title || ""} onChange={(e) => setStep(i, { title: e.target.value })} />
                  <button type="button" onClick={() => moveStep(i, -1)} disabled={i === 0} className="text-slate-400 disabled:opacity-30 hover:text-slate-700 px-1">↑</button>
                  <button type="button" onClick={() => moveStep(i, 1)} disabled={i === (form.steps || []).length - 1} className="text-slate-400 disabled:opacity-30 hover:text-slate-700 px-1">↓</button>
                  <button type="button" onClick={() => removeStep(i)} className="text-red-500 hover:text-red-700"><Trash size={14} /></button>
                </div>
                <textarea className={fieldClass} rows={3} placeholder="Step prayer text…" value={s.text || ""} onChange={(e) => setStep(i, { text: e.target.value })} />
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ---------- DONATIONS PANEL ----------

function DonationsTab() {
  const [data, setData] = useState({ items: [], total_usd: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/donations").then((r) => setData(r.data)).finally(() => setLoading(false)); }, []);
  return (
    <div data-testid="admin-donations-tab">
      <div className="flex justify-between items-start mb-3">
        <h3 className="font-heading text-lg text-slate-900">Donations</h3>
        <div className="text-right">
          <p className="text-xs text-slate-500">Confirmed / Pending</p>
          <p className="font-heading text-lg text-slate-900">
            <span className="text-emerald-600">${(data.confirmed_total_usd ?? 0).toLocaleString()}</span>
            <span className="text-slate-300 mx-1">·</span>
            <span className="text-amber-600">${(data.pending_total_usd ?? 0).toLocaleString()}</span>
          </p>
        </div>
      </div>
      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
        Payments are <b>MOCKED</b> — these are donation intents. Plug a real Stripe/crypto verifier into <code>/api/donations</code> to mark items <code>completed</code>.
      </p>
      {loading ? <p className="text-sm text-slate-500">Loading…</p> : data.items.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-12">No donations yet.</p>
      ) : (
        <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">User</th><th className="text-right px-3 py-2">Amount</th><th className="text-left px-3 py-2">Mode</th><th className="text-left px-3 py-2">Method</th><th className="text-left px-3 py-2">Status</th></tr>
            </thead>
            <tbody>
              {data.items.map((d) => (
                <tr key={d.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 text-slate-500 text-xs">{new Date(d.created_at).toLocaleString()}</td>
                  <td className="px-3 py-2 text-slate-700">{d.user_email}</td>
                  <td className="px-3 py-2 text-right font-medium text-slate-900">${d.amount_usd}</td>
                  <td className="px-3 py-2 text-slate-700">{d.mode}</td>
                  <td className="px-3 py-2 text-slate-700">{d.method}{d.network ? ` · ${d.network}/${d.token}` : ""}</td>
                  <td className="px-3 py-2"><span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800">{d.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------- EVENTS PANEL ----------

function EventsTab() {
  const [data, setData] = useState({ recent: [], summary: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/events").then((r) => setData(r.data)).finally(() => setLoading(false)); }, []);
  return (
    <div data-testid="admin-events-tab">
      <h3 className="font-heading text-lg text-slate-900 mb-3">Funnel & Events</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <div className="border border-slate-200 rounded-xl bg-white p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Top events</p>
          {loading ? <p className="text-sm text-slate-500">Loading…</p> : data.summary.length === 0 ? (
            <p className="text-sm text-slate-500">No events tracked yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-slate-500 text-[11px] uppercase tracking-wider">
                <tr><th className="text-left py-1">Event</th><th className="text-right py-1">Count</th><th className="text-right py-1">Users</th></tr>
              </thead>
              <tbody>
                {data.summary.map((e) => (
                  <tr key={e.name} className="border-t border-slate-100">
                    <td className="py-1.5 text-slate-800"><code className="text-xs">{e.name}</code></td>
                    <td className="py-1.5 text-right">{e.count}</td>
                    <td className="py-1.5 text-right">{e.unique_users}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="border border-slate-200 rounded-xl bg-white p-4 max-h-96 overflow-y-auto">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Recent (200)</p>
          {data.recent.map((e) => (
            <div key={e.id} className="text-xs py-1 border-b border-slate-100 last:border-0">
              <code className="text-slate-800">{e.name}</code>
              <span className="ml-2 text-slate-400">{new Date(e.created_at).toLocaleTimeString()}</span>
              {Object.keys(e.properties || {}).length > 0 && (
                <span className="ml-2 text-slate-500">{JSON.stringify(e.properties)}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- ROOT ----------

export default function AdminContentPanel() {
  const [tab, setTab] = useState("learn");
  return (
    <Tabs value={tab} onValueChange={setTab} data-testid="admin-content-panel">
      <TabsList className="bg-slate-100 mb-5">
        {[
          ["learn", "Learn"], ["community", "Community"], ["watch", "Watch"],
          ["journeys", "Prayer Journeys"], ["donations", "Donations"], ["events", "Events"],
        ].map(([v, l]) => (
          <TabsTrigger key={v} value={v} className="font-body text-xs data-[state=active]:bg-[#C4A265] data-[state=active]:text-white">
            {l}
          </TabsTrigger>
        ))}
      </TabsList>
      <TabsContent value="learn"><LearnTab /></TabsContent>
      <TabsContent value="community"><CommunityTab /></TabsContent>
      <TabsContent value="watch"><WatchTab /></TabsContent>
      <TabsContent value="journeys"><JourneysTab /></TabsContent>
      <TabsContent value="donations"><DonationsTab /></TabsContent>
      <TabsContent value="events"><EventsTab /></TabsContent>
    </Tabs>
  );
}
